/**
 * Shared RBAC API Hook
 * Provides API functions for RBAC operations in both Admin and Platform contexts
 */

import { useState, useCallback } from 'react'
import { api } from '../../api/client.js'
import type {
  Role,
  Permission,
  AuditLog,
  RoleAuditLog,
  CreateRoleInput,
  UpdateRoleInput,
  CreatePermissionInput,
  UpdatePermissionInput,
  PaginationParams,
} from './types.js'

interface UseRbacApiOptions {
  baseUrl: string // '/admin' or '/platform/rbac'
}

export function useRbacApi({ baseUrl }: UseRbacApiOptions) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const request = useCallback(
    async <T>(endpoint: string, options?: RequestInit): Promise<T> => {
      setLoading(true)
      setError(null)
      try {
        const url = `${baseUrl}${endpoint}`
        const method = options?.method || 'GET'
        
        let response
        if (method === 'GET') {
          response = await api.get<T>(url)
        } else if (method === 'POST') {
          response = await api.post<T>(url, options?.body ? JSON.parse(options.body as string) : {})
        } else if (method === 'PUT') {
          response = await api.put<T>(url, options?.body ? JSON.parse(options.body as string) : {})
        } else if (method === 'DELETE') {
          response = await api.delete<T>(url)
        } else {
          throw new Error(`Unsupported method: ${method}`)
        }
        
        return response
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An error occurred'
        setError(message)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [baseUrl]
  )

  // Role operations
  const listRoles = useCallback(
    async (params?: PaginationParams) => {
      const queryParams = new URLSearchParams()
      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.offset) queryParams.append('offset', params.offset.toString())
      
      const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
      const response = await request<{ roles: Role[]; summary: { total: number; withUsers: number; withoutUsers: number } }>(`/roles${query}`)
      return response
    },
    [request]
  )

  const getRole = useCallback(
    async (roleId: string) => {
      const response = await request<{ role: Role }>(`/roles/${roleId}`)
      return response.role
    },
    [request]
  )

  const createRole = useCallback(
    async (data: CreateRoleInput) => {
      const response = await request<{ role: Role }>(`/roles`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
      return response.role
    },
    [request]
  )

  const updateRole = useCallback(
    async (roleId: string, data: UpdateRoleInput) => {
      const response = await request<{ role: Role }>(`/roles/${roleId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      })
      return response.role
    },
    [request]
  )

  const deleteRole = useCallback(
    async (roleId: string) => {
      await request(`/roles/${roleId}`, { method: 'DELETE' })
    },
    [request]
  )

  // Permission operations
  const listPermissions = useCallback(
    async (params?: PaginationParams & { search?: string; domain?: string }) => {
      const queryParams = new URLSearchParams()
      // Default to a high limit so permission pickers (role editor) and the
      // permissions pages get the FULL catalog. The API paginates at 50 by
      // default, which truncates the alphabetical list before it reaches
      // conversations/voicemail/etc. — making those permissions impossible to
      // pick when editing a role.
      queryParams.append('limit', (params?.limit ?? 1000).toString())
      if (params?.offset) queryParams.append('offset', params.offset.toString())
      if (params?.search) queryParams.append('search', params.search)
      if (params?.domain) queryParams.append('domain', params.domain)
      
      const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
      const response = await request<{ permissions: Record<string, Permission[]>; pagination: any; summary: { total: number; system: number; custom: number } }>(`/permissions${query}`)
      return response
    },
    [request]
  )

  const getPermission = useCallback(
    async (permissionId: string) => {
      const response = await request<{ permission: Permission }>(`/permissions/${permissionId}`)
      return response.permission
    },
    [request]
  )

  const createPermission = useCallback(
    async (data: CreatePermissionInput) => {
      const response = await request<{ permission: Permission }>(`/permissions`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
      return response.permission
    },
    [request]
  )

  const updatePermission = useCallback(
    async (permissionId: string, data: UpdatePermissionInput) => {
      const response = await request<{ permission: Permission }>(`/permissions/${permissionId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      })
      return response.permission
    },
    [request]
  )

  const deletePermission = useCallback(
    async (permissionId: string) => {
      await request(`/permissions/${permissionId}`, { method: 'DELETE' })
    },
    [request]
  )

  // Audit log operations
  const listAuditLogs = useCallback(
    async (params?: PaginationParams & { userId?: string; actorId?: string; action?: string; from?: string; to?: string }) => {
      const queryParams = new URLSearchParams()
      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.offset) queryParams.append('offset', params.offset.toString())
      if (params?.userId) queryParams.append('userId', params.userId)
      if (params?.actorId) queryParams.append('actorId', params.actorId)
      if (params?.action) queryParams.append('action', params.action)
      if (params?.from) queryParams.append('from', params.from)
      if (params?.to) queryParams.append('to', params.to)
      
      const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
      // Admin uses /admin/permissions/audit, Platform uses /platform/rbac/audit
      const auditEndpoint = baseUrl === '/admin' ? '/permissions/audit' : '/audit'
      const response = await request<{ logs: AuditLog[]; total: number; limit: number; offset: number }>(`${auditEndpoint}${query}`)
      return response
    },
    [request, baseUrl]
  )

  const listRoleAuditLogs = useCallback(
    async (params?: PaginationParams & { action?: string; from?: string; to?: string }) => {
      const queryParams = new URLSearchParams()
      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.offset) queryParams.append('offset', params.offset.toString())
      if (params?.action) queryParams.append('action', params.action)
      if (params?.from) queryParams.append('from', params.from)
      if (params?.to) queryParams.append('to', params.to)
      
      const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
      const response = await request<{ logs: RoleAuditLog[]; total: number; limit: number; offset: number }>(`/roles/audit${query}`)
      return response
    },
    [request]
  )

  return {
    loading,
    error,
    // Roles
    listRoles,
    getRole,
    createRole,
    updateRole,
    deleteRole,
    // Permissions
    listPermissions,
    getPermission,
    createPermission,
    updatePermission,
    deletePermission,
    // Audit logs
    listAuditLogs,
    listRoleAuditLogs,
  }
}
